import React, {useEffect, useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {ProjectGuestPortalClient} from '../../infrastracture/api/ProjectGuestPortalClient'

// Mirrors the 16-char / mixed-case / digit / symbol rule ProjectGuestActivate.tsx and
// ChangeMyPasswordCommandValidator both enforce (same rule PasswordResetCompleteCommand uses).
const passwordSchema = Yup.object().shape({
  currentPassword: Yup.string().required('Current password is required'),
  newPassword: Yup.string()
    .required('Password is required')
    .min(16, 'Please choose a minimum of 16 characters')
    .matches(
      /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z0-9])/,
      'The password must contain at least one uppercase, one lowercase, one digit, and one symbol'
    ),
})

export function ProjectGuestProfile() {
  const [profile, setProfile] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState<{type: 'success' | 'danger'; message: string} | null>(null)

  useEffect(() => {
    ProjectGuestPortalClient.GetMyProfile().then((response) => {
      if (response.successful) setProfile(response.data)
      setLoading(false)
    })
  }, [])

  const formik = useFormik({
    initialValues: {currentPassword: '', newPassword: ''},
    validationSchema: passwordSchema,
    onSubmit: async (values, {resetForm, setSubmitting}) => {
      setStatus(null)
      const response = await ProjectGuestPortalClient.ChangePassword(values.currentPassword, values.newPassword)
      if (response.successful && response.data?.successful) {
        setStatus({type: 'success', message: 'Your password has been updated.'})
        resetForm()
      } else {
        setStatus({type: 'danger', message: 'Your current password was incorrect.'})
      }
      setSubmitting(false)
    },
  })

  return (
    <div className='d-flex flex-column gap-5'>
      <div className='card'>
        <div className='card-header'>
          <div className='card-title'>
            <h3>Profile</h3>
          </div>
        </div>
        <div className='card-body'>
          {loading ? (
            <div className='text-muted'>Loading...</div>
          ) : (
            <>
              <div className='mb-4'>
                <div className='fw-bolder'>Name</div>
                <div className='text-gray-600'>
                  {profile?.name} {profile?.surname}
                </div>
              </div>
              <div>
                <div className='fw-bolder'>Email</div>
                <div className='text-gray-600'>{profile?.email}</div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className='card'>
        <div className='card-header'>
          <div className='card-title'>
            <h3>Change Password</h3>
          </div>
        </div>
        <div className='card-body'>
          {status && <div className={`alert alert-${status.type}`}>{status.message}</div>}
          <form onSubmit={formik.handleSubmit} noValidate>
            <div className='fv-row mb-6'>
              <label className='form-label fw-bolder'>Current Password</label>
              <input
                type='password'
                autoComplete='off'
                {...formik.getFieldProps('currentPassword')}
                className={clsx('form-control form-control-solid', {
                  'is-invalid': formik.touched.currentPassword && formik.errors.currentPassword,
                })}
              />
              {formik.touched.currentPassword && formik.errors.currentPassword && (
                <div className='text-danger fs-7 mt-1'>{formik.errors.currentPassword}</div>
              )}
            </div>
            <div className='fv-row mb-6'>
              <label className='form-label fw-bolder'>New Password</label>
              <input
                type='password'
                autoComplete='off'
                {...formik.getFieldProps('newPassword')}
                className={clsx('form-control form-control-solid', {
                  'is-invalid': formik.touched.newPassword && formik.errors.newPassword,
                })}
              />
              {formik.touched.newPassword && formik.errors.newPassword && (
                <div className='text-danger fs-7 mt-1'>{formik.errors.newPassword}</div>
              )}
              <ul className='text-muted fs-7 mt-2 mb-0 ps-4'>
                <li>At least 16 characters</li>
                <li>At least one uppercase letter</li>
                <li>At least one lowercase letter</li>
                <li>At least one digit</li>
                <li>At least one symbol</li>
              </ul>
            </div>
            <button type='submit' className='btn btn-primary' disabled={formik.isSubmitting}>
              {formik.isSubmitting ? 'Saving...' : 'Update Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
